import type { CSSProperties } from 'react';
import React from 'react';
import { useWordsHappenTheme } from '../../theme';
import styles from './tile.module.scss';

export type TileBonus = 'letter-2x' | 'letter-3x' | 'word-2x' | 'word-3x';

export interface TileProps {
  letter: string;
  points?: number;
  bonus?: TileBonus;
  placed?: boolean;
  ghost?: boolean;
  highlight?: boolean;
  mini?: boolean;
  size?: number;
  className?: string;
}

const DEFAULT_POINTS: Record<1 | 2 | 3, number> = { 1: 1, 2: 4, 3: 8 };

const BONUS_LABELS: Record<TileBonus, string> = {
  'letter-2x': '2×',
  'letter-3x': '3×',
  'word-2x': 'W2×',
  'word-3x': 'W3×',
};

const BONUS_BG: Record<TileBonus, string> = {
  'letter-2x': 'var(--wh-teal)',
  'letter-3x': 'var(--wh-teal)',
  'word-2x': 'var(--wh-plum)',
  'word-3x': 'var(--wh-plum)',
};

export const Tile: React.FC<TileProps> = ({
  letter,
  points,
  bonus,
  placed = false,
  ghost = false,
  highlight = false,
  mini = false,
  size = 44,
  className,
}) => {
  const { theme } = useWordsHappenTheme();
  const [pressed, setPressed] = React.useState(false);

  let displayLetter = letter;
  if (letter.length > 3) {
    // biome-ignore lint/suspicious/noConsole: spec requires a warning for oversized letter props
    console.warn(`[Tile] letter "${letter}" exceeds 3 characters; truncating to "${letter.slice(0, 3)}".`);
    displayLetter = letter.slice(0, 3);
  }

  const charCount = Math.min(Math.max(displayLetter.length, 1), 3) as 1 | 2 | 3;
  const width = charCount === 1 ? size : charCount === 2 ? size * 1.5 : size * 2;

  const resolvedPoints = points ?? DEFAULT_POINTS[charCount];
  const showPoints = !ghost && !mini;

  const isMeadow = theme === 'meadow';
  const isPressed = isMeadow && pressed && !placed && !ghost;

  const inset = mini ? 2 : 4;
  const letterFontSize = mini ? Math.round(size * 0.48) : Math.round(size * 0.52);
  const badgeFontSize = Math.round(size * 0.28);
  const bonusFontSize = Math.max(8, Math.round(size * 0.22));

  const ariaLabel = ghost ? 'Empty tile slot' : displayLetter;

  const outerCls = [
    styles.tile,
    ghost ? styles.ghost : styles.solid,
    !ghost && placed ? styles.placed : null,
    !ghost && highlight ? styles.highlight : null,
    isMeadow ? styles.meadow : null,
    isPressed ? styles.pressed : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={outerCls}
      style={{ '--tile-width': `${width}px`, '--tile-height': `${size}px` } as CSSProperties}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
    >
      {!ghost && (
        <div
          className={styles.inner}
          style={
            {
              '--tile-inset': `${inset}px`,
              '--inner-radius': `calc(var(--wh-radius-sm) - ${inset - 1}px)`,
            } as CSSProperties
          }
        >
          <span className={styles.letter} style={{ '--letter-font-size': `${letterFontSize}px` } as CSSProperties}>
            {displayLetter}
          </span>

          {showPoints && (
            <span
              aria-hidden="true"
              className={styles.points}
              style={{ '--badge-font-size': `${badgeFontSize}px` } as CSSProperties}
            >
              {resolvedPoints}
            </span>
          )}
        </div>
      )}

      {bonus && !ghost && (
        <span
          aria-hidden="true"
          className={styles.bonus}
          style={
            {
              '--bonus-font-size': `${bonusFontSize}px`,
              '--bonus-bg': BONUS_BG[bonus],
            } as CSSProperties
          }
        >
          {BONUS_LABELS[bonus]}
        </span>
      )}
    </div>
  );
};
